import React, { useState, useEffect } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import PropTypes from 'prop-types';
import api from '@services/api';
import './DynamicBorrowingFields.scss';

/**
 * Dynamic borrowing fields component that renders different input types
 * based on category-level field definitions for borrowings/reservations
 *
 * @param {number} categoryId - ID of the resource category
 * @param {string|Array} context - Context(s) for which to show fields ("reservation", "borrowing", "return")
 * @param {Object} existingFields - Existing dynamic field values
 * @param {Function} onChange - Callback function called when fields change
 * @param {boolean} disabled - Whether the form should be disabled
 */
const DynamicBorrowingFields = ({
  categoryId,
  context,
  existingFields = {},
  onChange,
  disabled = false,
}) => {
  const { t } = useLanguage();
  const [fieldDefinitions, setFieldDefinitions] = useState({});
  const [fieldValues, setFieldValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Normalize context to array
  const contexts = Array.isArray(context) ? context : [context];

  // Initialize field values from existing fields
  useEffect(() => {
    if (existingFields && Object.keys(existingFields).length > 0) {
      // Normalize: recursively strip enriched {value, definition} format to raw values
      const normalized = {};
      Object.entries(existingFields).forEach(([key, val]) => {
        while (val && typeof val === 'object' && 'value' in val && 'definition' in val) {
          val = val.value;
        }
        normalized[key] = val;
      });
      setFieldValues(normalized);
    }
  }, [existingFields]);

  // Fetch field definitions from category
  useEffect(() => {
    const fetchFieldDefinitions = async () => {
      if (!categoryId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.categories.getBorrowingFieldDefinitions(categoryId);
        setFieldDefinitions(response.data.borrowing_field_definitions || {});
      } catch (err) {
        console.error('Failed to fetch borrowing field definitions:', err);
        setError(err.message || 'Failed to fetch field definitions');
      } finally {
        setLoading(false);
      }
    };

    fetchFieldDefinitions();
  }, [categoryId]);

  // Handle field value changes
  const handleFieldChange = (fieldKey, value) => {
    const newValues = {
      ...fieldValues,
      [fieldKey]: value
    };
    setFieldValues(newValues);

    // Call parent onChange callback
    if (onChange) {
      onChange(newValues);
    }
  };

  // Filter field definitions based on current context(s)
  const getApplicableFields = () => {
    return Object.entries(fieldDefinitions).filter(([fieldKey, definition]) => {
      const fieldContexts = definition.contexts || [];
      // If no contexts defined, field applies to all contexts
      if (fieldContexts.length === 0) {
        return true;
      }
      // Check if any of the current contexts match the field's contexts
      return contexts.some(ctx => fieldContexts.includes(ctx));
    });
  };

  // Render input based on field type
  const renderFieldInput = (fieldKey, definition) => {
    const value = fieldValues[fieldKey];
    const label = definition.label || fieldKey;
    const helpText = definition.help_text;
    const placeholder = definition.placeholder_text;
    const required = definition.required || false;

    switch (definition.type) {
      case 'string':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className="form-control"
          />
        );

      case 'integer':
        return (
          <input
            type="number"
            step="1"
            value={value !== null && value !== undefined ? value : ''}
            onChange={(e) => {
              const intValue = e.target.value === '' ? null : parseInt(e.target.value, 10);
              handleFieldChange(fieldKey, intValue);
            }}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className="form-control"
          />
        );

      case 'float':
        return (
          <input
            type="number"
            step="0.01"
            value={value !== null && value !== undefined ? value : ''}
            onChange={(e) => {
              const floatValue = e.target.value === '' ? null : parseFloat(e.target.value);
              handleFieldChange(fieldKey, floatValue);
            }}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className="form-control"
          />
        );

      case 'boolean':
        return (
          <div className="boolean-input">
            <label className="boolean-option">
              <input
                type="radio"
                name={fieldKey}
                value="true"
                checked={value === true}
                onChange={() => handleFieldChange(fieldKey, true)}
                disabled={disabled}
              />
              <span>{t('common.yes') || 'Yes'}</span>
            </label>
            <label className="boolean-option">
              <input
                type="radio"
                name={fieldKey}
                value="false"
                checked={value === false}
                onChange={() => handleFieldChange(fieldKey, false)}
                disabled={disabled}
              />
              <span>{t('common.no') || 'No'}</span>
            </label>
            {!required && (
              <label className="boolean-option">
                <input
                  type="radio"
                  name={fieldKey}
                  value="unset"
                  checked={value === null || value === undefined}
                  onChange={() => handleFieldChange(fieldKey, null)}
                  disabled={disabled}
                />
                <span>{t('common.unset') || 'Unset'}</span>
              </label>
            )}
          </div>
        );

      case 'select':
        const options = definition.options || [];
        return (
          <select
            value={value || ''}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value || null)}
            disabled={disabled}
            required={required}
            className="form-control"
          >
            <option value="">
              {placeholder || t('common.selectOption') || 'Select an option...'}
            </option>
            {options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className="form-control"
          />
        );
    }
  };

  // Don't render anything if no category
  if (!categoryId) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="dynamic-borrowing-fields dynamic-borrowing-fields--loading">
        <p>{t('common.loading') || 'Loading...'}</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="dynamic-borrowing-fields dynamic-borrowing-fields--error">
        <p className="error-message">
          {t('borrowingFields.loadError') || 'Error loading fields'}: {error}
        </p>
      </div>
    );
  }

  const applicableFields = getApplicableFields();

  // Don't render if no applicable fields
  if (applicableFields.length === 0) {
    return null;
  }

  // Separate required and optional fields
  const requiredFields = applicableFields.filter(([, def]) => def.required);
  const optionalFields = applicableFields.filter(([, def]) => !def.required);

  return (
    <div className="dynamic-borrowing-fields">
      <h4 className="dynamic-borrowing-fields__title">
        {t('borrowingFields.additionalInformation') || 'Additional Information'}
      </h4>

      {requiredFields.length > 0 && (
        <div className="dynamic-borrowing-fields__section">
          <h5 className="dynamic-borrowing-fields__section-title">
            {t('borrowingFields.requiredFields') || 'Required Fields'}
          </h5>
          {requiredFields.map(([fieldKey, definition]) => (
            <div key={fieldKey} className="form-group dynamic-field">
              <label htmlFor={fieldKey} className="form-label">
                {definition.label || fieldKey}
                <span className="required-indicator"> *</span>
              </label>
              {definition.help_text && (
                <p className="form-help-text">{definition.help_text}</p>
              )}
              {renderFieldInput(fieldKey, definition)}
            </div>
          ))}
        </div>
      )}

      {optionalFields.length > 0 && (
        <div className="dynamic-borrowing-fields__section">
          {requiredFields.length > 0 && (
            <h5 className="dynamic-borrowing-fields__section-title">
              {t('borrowingFields.optionalFields') || 'Optional Fields'}
            </h5>
          )}
          {optionalFields.map(([fieldKey, definition]) => (
            <div key={fieldKey} className="form-group dynamic-field">
              <label htmlFor={fieldKey} className="form-label">
                {definition.label || fieldKey}
              </label>
              {definition.help_text && (
                <p className="form-help-text">{definition.help_text}</p>
              )}
              {renderFieldInput(fieldKey, definition)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

DynamicBorrowingFields.propTypes = {
  categoryId: PropTypes.number,
  context: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]).isRequired,
  existingFields: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default DynamicBorrowingFields;
